import { Box, Button, Modal, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Pencil, Plus, Trash } from "phosphor-react";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { fdelete, fget } from "../../API/callsAPI";
import commonStyles from "../../Utils/commonStyles";
import DateAndTimeViewer from "../Utilities/DateAndTimeViewer/DateAndTimeViewer.component";
import Loader from "../Utilities/Loader/Loader";
import CreateOrEditCustomPageModal from "./CreateOrEditCustomPageModal";
import UsersViewer from "../Utilities/UsersViewer/UsersViewer";
import DeleteModal from "../Utilities/DeleteModal/DeleteModal.component";
import ErrorOrSuccessMessage from "../Utilities/ErrorOrSuccessMessage/ErrorOrSuccessMessage.component";
import NoRowsSvg from "./NoRowsSvg";
import CreateNewCustomPageModal from "./CreateNewCustomPageModal";

const CustomPageOverview = ({ params }) => {
  const commonClasses = commonStyles();
  const history = useHistory();
  const [isCreateNewCustomPageModalOpen, setIsCreateNewCustomPageModalOpen] =
    useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [rows, setRows] = useState([]);
  const [deletePageData, setDeletePageData] = useState({
    isOpen: false,
    page: null,
  });
  const [editCustomPageData, setEditCustomPageData] = useState({
    isOpen: false,
    page: null,
  });
  const [snackValue, setSnackValue] = useState({
    isOpen: false,
    message: "",
    isError: false,
  });

  const handleGetCustomPages = async () => {
    try {
      const res = await fget({
        url: `trackables/custom-page/?project=${params?.projectId}`,
      });
      setIsLoaded(true);
      const customPageData = res.data.results.map((page) => {
        return {
          id: page.id,
          name: page.name,
          description: page.description,
          sharedTo: page.shared_to,
          lastModified: page.updated_at,
          createdBy: page.created_by,
          fields: page.fields,
          trackable: page.trackable,
          page_config: page.page_config,
        };
      });
      setRows(customPageData);
    } catch (error) {
      console.log(error);
      setIsLoaded(true);
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Page Name",
      flex: 1,
      renderCell: (params) => <Typography>{params.value}</Typography>,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      renderCell: (params) => (
        <Typography sx={{ color: "gray" }}>{params.value ?? "--"}</Typography>
      ),
    },
    {
      field: "sharedTo",
      headerName: "Shared To",
      flex: 1,
      renderCell: (params) => (
        <Typography>{params.value ?? "Shared To"}</Typography>
      ),
    },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 1,
      renderCell: (params) => (
        <UsersViewer
          assignedUser={[params?.value]}
          selectedTask={""}
          editable="no"
        />
      ),
    },
    {
      field: "lastModified",
      headerName: "Last Modified",
      flex: 1,
      renderCell: (params) => (
        <DateAndTimeViewer dateAndTime={new Date(params.value)} />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%",
            gap: "8px",
          }}
        >
          <Button
            sx={{ color: "white", minWidth: 0, padding: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              setEditCustomPageData({ isOpen: true, page: params.row });
            }}
          >
            <Pencil size={22} />
          </Button>
          <Button
            sx={{ color: "white", minWidth: 0, padding: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              setDeletePageData({ isOpen: true, page: params.row });
            }}
          >
            <Trash size={22} />
          </Button>
        </Box>
      ),
    },
  ];
  const handleDelete = async () => {
    try {
      await fdelete({
        url: `trackables/custom-page/${deletePageData.page.id}/`,
      });
      setSnackValue({
        isOpen: true,
        message: `SuccessFully deleted page`,
        isError: false,
      });
      handleGetCustomPages();
      setDeletePageData({ isOpen: false, page: null });
    } catch (error) {
      setSnackValue({
        isOpen: true,
        message: "Error On deleting custom page",
        isError: true,
      });
    }
  };

  const handleRowClick = (internalParams) => {
    history.push(`/${params.projectId}/custom-page/${internalParams.id}`);
  };

  useEffect(() => {
    handleGetCustomPages();
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <ErrorOrSuccessMessage snackValue={snackValue} />
      {isLoaded ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <Typography
              sx={{
                fontSize: "20px !important",
                fontWeight: 500,
              }}
            >
              Custom Pages
            </Typography>
            <Button
              onClick={() => setIsCreateNewCustomPageModalOpen(true)}
              variant="contained"
              sx={{
                backgroundColor: "#6C65D9",
                borderRadius: "10px",
                textTransform: "none",
              }}
              startIcon={<Plus />}
            >
              <Typography>Create New Page</Typography>
            </Button>
          </Box>
          <Box className={commonClasses.customPageTableWidth}>
            <DataGrid
              rows={rows}
              columns={columns}
              onRowClick={handleRowClick}
              hideFooterPagination
              disableColumnReorder
              slots={{
                noRowsOverlay: () => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    <NoRowsSvg />
                    <Typography
                      sx={{
                        textAlign: "center",
                        mt: 4,
                        fontSize: "22px !important",
                        color: "white",
                      }}
                    >
                      You have no custom pages.
                      <br />
                      Create a page to get started!
                    </Typography>
                  </Box>
                ),
              }}
              sx={{
                border: "none",
                "--DataGrid-rowBorderColor": "#606479",
                "& .MuiDataGrid-columnHeaders": {
                  color: "gray",
                  fontSize: {
                    xs: "12px",
                    sm: "14px",
                    md: "14px",
                    lg: "16px",
                    xl: "16px",
                  },
                },
                "& .MuiDataGrid-columnHeader": {
                  color: "gray",
                  borderBottom: "1px solid #606479",
                },
                "& .MuiDataGrid-withBorderColor": {
                  border: "none !important",
                },
                "& .MuiDataGrid-cell": {
                  color: "white",
                  display: "flex",
                  justifyContent: "space-between",
                  borderColor: "#606479",
                  alignItems: "center",
                },
                "& .MuiDataGrid-row": {
                  borderBottom: "none",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#252A38",
                  },
                },
                "& .MuiDataGrid-virtualScroller": {
                  marginBottom: 0,
                },
                "& .MuiDataGrid-footerContainer": {
                  display: "none",
                },
                "& .MuiDataGrid-main": {
                  paddingBottom: "0px",
                },
                "& .MuiDataGrid-container--top [role='row'], & .MuiDataGrid-container--bottom [role='row']":
                  {
                    backgroundColor: "#1B1D28 !important",
                    borderColor: "gray",
                  },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeader:focus": {
                  outline: "none",
                },
              }}
            />
          </Box>

          <Modal
            open={editCustomPageData.isOpen}
            onClose={() => setEditCustomPageData({ isOpen: false, page: null })}
            className={commonClasses.flexCenter}
          >
            <CreateOrEditCustomPageModal
              type="edit"
              projectId={params.projectId}
              handleClose={() =>
                setEditCustomPageData({ isOpen: false, page: null })
              }
              refreshData={handleGetCustomPages}
              setSnackValue={setSnackValue}
              editData={editCustomPageData.page}
            />
          </Modal>
          <Modal
            open={isCreateNewCustomPageModalOpen}
            onClose={() => setIsCreateNewCustomPageModalOpen(false)}
            className={commonClasses.flexCenter}
          >
            <CreateNewCustomPageModal
              handleClose={() => setIsCreateNewCustomPageModalOpen(false)}
              refreshData={handleGetCustomPages}
              setSnackValue={setSnackValue}
              projectId={params.projectId}
            />
          </Modal>
          <Modal
            open={deletePageData.isOpen}
            onClose={() => setDeletePageData({ isOpen: false, page: null })}
            className={commonClasses.flexCenter}
          >
            <DeleteModal
              type="Custom Page"
              name={deletePageData.page?.name}
              handleCloseDeleteModal={() =>
                setDeletePageData({ isOpen: false, page: null })
              }
              handleDelete={handleDelete}
            />
          </Modal>
        </>
      ) : (
        <Loader />
      )}
    </Box>
  );
};

export default CustomPageOverview;
